/**
 * Where the rules surface's URLs lead after the Automations port (CONTRACTS
 * 10.1): the four-tab Automations hub at /rules, the old five-tab hub kept at
 * /rules/legacy as "Rules (classic)", and every older address that must still
 * land somewhere meaningful.
 *
 * Pure functions, no React, so routes.test.tsx can walk the whole table and
 * the redirect components in AppRoutes.tsx / AutomationsHub.tsx stay one line
 * each. Nothing here fetches or writes.
 *
 * The old addresses and where they go:
 *   /rules?tab=rules                 -> /rules?tab=automations
 *   /rules?tab=runs (and /runs)      -> /rules?tab=activity, keeping rule_id and run;
 *                                       a status list naming a failure -> state=failed
 *   /rules?tab=approvals, /approvals -> /rules?tab=activity&state=waiting
 *   /rules?tab=tags|coverage, /tags  -> /rules/legacy?tab=tags|coverage
 *   /rules/:id                       -> /rules?tab=activity&rule_id=:id (keeps run)
 *   /rules/:id/tree, /:id/edit-legacy, /rules/new-legacy
 *                                    -> the classic editor under /rules/legacy
 */
import type { ActivityFilter } from '../../lib/automations/contracts'
import { FAIL_STATUSES } from '../../lib/runStatus'

// ── The Automations hub (/rules) ──────────────────────────────────────────

export type HubTab = 'automations' | 'activity' | 'metrics' | 'filters'

export const HUB_TABS: ReadonlyArray<{ key: HubTab; label: string }> = [
  { key: 'automations', label: 'Automations' },
  { key: 'activity',    label: 'Activity' },
  { key: 'metrics',     label: 'Custom metrics' },
  { key: 'filters',     label: 'Saved filters' },
]

const HUB_TAB_KEYS: ReadonlySet<string> = new Set(HUB_TABS.map(t => t.key))

export const HUB_PATH = '/rules'

/** The old hub, kept as a fallback while the new pages bed in. */
export const CLASSIC_HUB_PATH = '/rules/legacy'

export type ClassicTab = 'rules' | 'runs' | 'approvals' | 'tags' | 'coverage'

export const CLASSIC_TABS: ReadonlyArray<{ key: ClassicTab; label: string }> = [
  { key: 'rules',     label: 'Rules' },
  { key: 'runs',      label: 'Runs' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'tags',      label: 'Tags' },
  // Phase 10 Build 33. Appended last on purpose: the tab order is muscle
  // memory for the team.
  { key: 'coverage',  label: 'Automation coverage' },
]

const CLASSIC_TAB_KEYS: ReadonlySet<string> = new Set(CLASSIC_TABS.map(t => t.key))

/** The tab a canonical /rules URL shows. Missing or unknown -> automations. */
export function hubTab(search: string): HubTab {
  const raw = new URLSearchParams(search).get('tab')
  return raw && HUB_TAB_KEYS.has(raw) ? (raw as HubTab) : 'automations'
}

/** The tab a /rules/legacy URL shows. Missing or unknown -> rules (as before). */
export function classicTab(search: string): ClassicTab {
  const raw = new URLSearchParams(search).get('tab')
  return raw && CLASSIC_TAB_KEYS.has(raw) ? (raw as ClassicTab) : 'rules'
}

/**
 * Where a /rules URL that still uses one of the old hub's tab ids should
 * really be, or null when it is already a URL of the new hub. The old ids
 * keep working because CommandCenter, RulesPreflight, SuggestionDetailModal
 * and people's bookmarks still link them.
 */
export function hubRedirect(search: string): string | null {
  const params = new URLSearchParams(search)
  const tab = params.get('tab')
  switch (tab) {
    case 'rules': {
      params.set('tab', 'automations')
      return `${HUB_PATH}?${params.toString()}`
    }
    case 'runs':
      return activityUrl(params)
    case 'approvals':
      return activityUrl(params, 'waiting')
    case 'tags':
    case 'coverage':
      return `${CLASSIC_HUB_PATH}?tab=${tab}`
    default:
      return null
  }
}

/** The URL a click on a hub tab goes to. Every other parameter belongs to
 *  the tab being left (Activity's rule_id, state, run), so none is kept. */
export function hubTabUrl(tab: HubTab): string {
  return `${HUB_PATH}?tab=${tab}`
}

// ── Activity deep links ───────────────────────────────────────────────────

/** A positive integer id from a query value, or null (NaN, 0 and junk are ignored). */
function positiveId(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null
  const n = Number(raw)
  return Number.isSafeInteger(n) && n > 0 ? n : null
}

/**
 * The Activity state a legacy Runs `status` filter maps to: `failed` when the
 * list names a failed run ('errors', 'failed', or any status runStatus.ts
 * classes as a failure, which covers CommandCenter's ERROR_RUNS_FILTER);
 * otherwise no state, i.e. Everything. Activity filters change rows, not
 * runs, so no other run status has an honest row-state twin.
 */
export function activityStateFromRunStatus(status: string | null): ActivityFilter | null {
  if (!status) return null
  const parts = status.split(',').map(s => s.trim()).filter(Boolean)
  return parts.some(s => s === 'errors' || s === 'failed' || FAIL_STATUSES.has(s)) ? 'failed' : null
}

/**
 * /rules?tab=activity built from an old Runs or Approvals link: keeps the
 * rule filter (rule_id) and the run a link pointed at (run), turns a failure
 * status list into state=failed, and drops the Runs page's own filters (date
 * preset, sort, paging, dry-run), which Activity does not have.
 */
export function activityUrl(from: URLSearchParams, state?: ActivityFilter): string {
  const out = new URLSearchParams()
  out.set('tab', 'activity')
  const ruleId = positiveId(from.get('rule_id'))
  if (ruleId !== null) out.set('rule_id', String(ruleId))
  const run = positiveId(from.get('run'))
  if (run !== null) out.set('run', String(run))
  const s = state ?? activityStateFromRunStatus(from.get('status'))
  if (s && s !== 'all') out.set('state', s)
  return `${HUB_PATH}?${out.toString()}`
}

// ── Redirects for retired paths (the <LegacyRedirect kind> routes) ────────

export type LegacyRedirectKind =
  /** /runs */
  | 'runs'
  /** /approvals */
  | 'approvals'
  /** /tags */
  | 'tags'
  /** /rules/:id, the read-only rule page retired long ago */
  | 'rule'
  /** /rules/:id/tree and /rules/:id/edit-legacy, the retired raw-JSON and flat editors */
  | 'classic-edit'
  /** /rules/new-legacy */
  | 'classic-new'

/** Where a retired path goes. `id` is the route's `:id`; `search` is the
 *  incoming query string (with or without its leading `?`). */
export function legacyRedirectTarget(
  kind: LegacyRedirectKind,
  id: string | undefined,
  search: string,
): string {
  const params = new URLSearchParams(search)
  const query = search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : ''
  switch (kind) {
    case 'runs':
      return activityUrl(params)
    case 'approvals':
      return activityUrl(params, 'waiting')
    case 'tags':
      return `${CLASSIC_HUB_PATH}?tab=tags`
    case 'rule': {
      // The id in the path wins over any rule_id in the query.
      if (id !== undefined) params.set('rule_id', id)
      return activityUrl(params)
    }
    case 'classic-edit':
      return `${CLASSIC_HUB_PATH}/${encodeURIComponent(id ?? '')}/edit${query}`
    case 'classic-new':
      return `${CLASSIC_HUB_PATH}/new${query}`
  }
}

// ── The rule tab strip (components/RuleEditorTabs.tsx) ────────────────────

export type RuleTabKey = 'edit' | 'runs' | 'backtest'

/** True on a URL of the classic pages, where the strip keeps the classic links. */
export function isClassicPath(pathname: string): boolean {
  return pathname === CLASSIC_HUB_PATH || pathname.startsWith(`${CLASSIC_HUB_PATH}/`)
}

/**
 * Where each tab of the per-rule strip goes. Inside the classic hub it stays
 * classic (the old wizard and the old Runs tab), so a person who went back to
 * the classic pages is not dropped into the new ones by the strip. Everywhere
 * else "runs" is the rule's Activity.
 */
export function ruleTabHref(key: RuleTabKey, ruleId: number, classic: boolean): string {
  switch (key) {
    case 'edit':
      return classic ? `${CLASSIC_HUB_PATH}/${ruleId}/edit` : `/rules/${ruleId}/edit`
    case 'runs':
      return classic ? `${CLASSIC_HUB_PATH}?tab=runs&rule_id=${ruleId}` : `${HUB_PATH}?tab=activity&rule_id=${ruleId}`
    case 'backtest':
      return `/rules/${ruleId}/backtest`
  }
}

/** The label of the strip's run-history tab: the classic page is called Runs,
 *  its replacement Activity. */
export function ruleRunsTabLabel(classic: boolean): string {
  return classic ? 'Runs' : 'Activity'
}

/** The new page a classic tab was replaced by, for the classic hub's
 *  "Open Automations" link. */
export function modernEquivalent(tab: ClassicTab): string {
  switch (tab) {
    case 'runs':      return hubTabUrl('activity')
    case 'approvals': return `${HUB_PATH}?tab=activity&state=waiting`
    default:          return HUB_PATH
  }
}
